import { useContext } from 'react';

import { Grid } from '@mui/material';
import moment from 'moment';

import { DataTable, Typography } from 'design_system/src';
import {
	PalencaAccountData,
	PalencaEarning,
	PalencaProfileData,
} from 'models/palenca/interfaces/palenca.interface';
import { ozonerContext } from '../OzonerContext';

const metricsLabels: Record<string, string> = {
	acceptance_rate: 'Tasa de aceptación',
	cancellation_rate: 'Tasa de cancelación',
	rating: 'Calificación',
	lifetime_trips: 'Viajes totales',
	time_since_first_trip: 'Tiempo desde el primer viaje',
	level_name: 'Nivel',
	debt_pending: 'Deuda pendiente',
	debt_paid: 'Deuda pagada',
	activation_status: 'Estado de activación',
	thumps_up: 'Thumbs up',
};

const capitalize = (value?: string) =>
	value ? value.charAt(0).toUpperCase() + value.slice(1) : undefined;

const formatCurrencyAmount = (amount: number, currency?: string) => {
	try {
		return new Intl.NumberFormat('es-MX', {
			style: 'currency',
			currency: (currency || 'MXN').toUpperCase(),
			maximumFractionDigits: 2,
		}).format(amount);
	} catch {
		return `${amount.toLocaleString('es-MX')} ${(
			currency || ''
		).toUpperCase()}`;
	}
};

const sumEarnings = (earnings: PalencaEarning[]) =>
	earnings.reduce((sum, earning) => sum + (earning.amount ?? 0), 0);

function PalencaAccountSection({ data }: { data: PalencaAccountData }) {
	// Palenca's `/profile` payload is normally a sibling of `employment`, but
	// some sandbox accounts nest it under `employment.profile` instead — try
	// both so the tab doesn't go blank depending on which shape a given
	// account was stored with.
	const profileData:
		| (PalencaProfileData & { earnings?: PalencaEarning[] })
		| undefined =
		data.profile ??
		(data.employment as { profile?: PalencaProfileData })?.profile;
	const earnings: PalencaEarning[] =
		data.earnings ?? profileData?.earnings ?? [];
	const currency = earnings[0]?.currency;

	const now = moment();
	const oneWeekAgo = moment().subtract(7, 'days');
	const threeMonthsAgo = moment().subtract(3, 'months');

	const lastWeekEarnings = earnings.filter(
		(earning) =>
			earning.earning_date &&
			moment(earning.earning_date).isSameOrAfter(oneWeekAgo, 'day'),
	);
	const last3MonthsEarnings = earnings.filter(
		(earning) =>
			earning.earning_date &&
			moment(earning.earning_date).isSameOrAfter(threeMonthsAgo, 'day'),
	);
	const last3MonthsTotal = sumEarnings(last3MonthsEarnings);
	const daysInWindow = now.diff(threeMonthsAgo, 'days') || 1;

	const rfcEntry = profileData?.ids_info?.find(
		(id) =>
			id.name?.toLowerCase() === 'rfc' || id.type?.toLowerCase() === 'rfc',
	);

	const overview = [
		{ label: 'Plataforma', value: capitalize(data.account?.platform) ?? '--' },
		{ label: 'País', value: data.account?.country?.toUpperCase() ?? '--' },
		{
			label: 'Estado de conexión',
			value:
				data.account?.status === 'success'
					? 'Exitosa'
					: data.account?.status ?? '--',
			valueColor: data.account?.status === 'success' ? 'green_300' : 'red_300',
		},
	];

	const profileRows = [
		{ label: 'Nombres', value: profileData?.profile?.first_name ?? '--' },
		{ label: 'Apellidos', value: profileData?.profile?.last_name ?? '--' },
		{ label: 'Email', value: profileData?.profile?.email ?? '--' },
		{ label: 'Dirección', value: profileData?.profile?.address ?? '--' },
		{ label: 'Ciudad', value: profileData?.profile?.city_name ?? '--' },
		{ label: 'Rol', value: profileData?.profile?.worker_role ?? '--' },
		...(rfcEntry ? [{ label: 'RFC', value: rfcEntry.value ?? '--' }] : []),
	];

	const earningsSummary = [
		{
			label: 'Ingresos última semana',
			value: formatCurrencyAmount(sumEarnings(lastWeekEarnings), currency),
		},
		{
			label: 'Ingresos últimos 3 meses',
			value: formatCurrencyAmount(last3MonthsTotal, currency),
		},
		{
			label: 'Promedio diario (3 meses)',
			value: formatCurrencyAmount(last3MonthsTotal / daysInWindow, currency),
		},
	];

	const metricsRows = Object.entries(profileData?.metrics_info ?? {})
		.filter(
			([, value]) => value !== null && value !== undefined && value !== '',
		)
		.map(([key, value]) => ({
			label: metricsLabels[key] ?? key,
			value: String(value),
		}));

	return (
		<div className="display_flex flex_col flex_gap_md m_b_lg">
			<div className="display_flex flex_align_center flex_gap_md">
				{profileData?.profile?.picture_url && (
					<img
						src={profileData.profile.picture_url}
						alt="perfil palenca"
						className="dim_xl br_circle overflow_hidden"
					/>
				)}
				<Typography scale="large" weight="600">
					{`Plataforma: ${capitalize(data.account?.platform) ?? '--'}`}
					{' - '}
					{[profileData?.profile?.first_name, profileData?.profile?.last_name]
						.filter(Boolean)
						.join(' ') || '--'}
				</Typography>
			</div>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<DataTable title="Resumen" data={overview} />
				</Grid>
				<Grid item xs={12} md={6}>
					<DataTable title="Perfil" data={profileRows} />
				</Grid>
				<Grid item xs={12} md={6}>
					<DataTable title="Ingresos" data={earningsSummary} />
				</Grid>
				{metricsRows.length > 0 && (
					<Grid item xs={12} md={6}>
						<DataTable title="Métricas" data={metricsRows} />
					</Grid>
				)}
			</Grid>
		</div>
	);
}

function PalencaOzoner() {
	const { ozoner } = useContext(ozonerContext);
	const accounts = ozoner?.palencaData ? Object.values(ozoner.palencaData) : [];

	if (!accounts.length) {
		return (
			<Typography scale="medium" weight="400" className="p_md text_center">
				Este ozoner no tiene cuentas de Palenca conectadas.
			</Typography>
		);
	}

	return (
		<div className="p_md">
			{accounts.map((data, index) => (
				<PalencaAccountSection
					// eslint-disable-next-line react/no-array-index-key
					key={data.account?.account_id ?? index}
					data={data}
				/>
			))}
		</div>
	);
}

export default PalencaOzoner;
